import UserProfile from '@/components/auth/UserProfile';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
    ChevronDown,
    LogOut,
    Menu,
    Moon,
    Settings,
    Sun,
    User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header = ({ onMenuClick }: HeaderProps) => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  // const [showNotifications, setShowNotifications] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // const notificationRef = useRef<HTMLDivElement>(null);

  const handleProfileClick = () => {
    setShowUserMenu(false);
    setShowProfileDialog(true);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    logout();
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      // if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
      //   setShowNotifications(false);
      // }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menus on escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
        // setShowNotifications(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-30 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onMenuClick}
              className={cn(
                "lg:hidden h-9 w-9 rounded-lg",
                "hover:bg-accent hover:text-accent-foreground",
                "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "transition-all duration-200 active:scale-95"
              )}
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Right Section */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            {/* <div className="relative" ref={notificationRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  setShowUserMenu(false);
                }}
                className={cn(
                  "h-9 w-9 rounded-lg relative",
                  "hover:bg-accent hover:text-accent-foreground",
                  "focus-visible:ring-2 focus-visible:ring-ring",
                  "transition-all duration-200"
                )}
                aria-label="Notifications"
              >
                <Bell className="h-4 w-4" />
                <span className="absolute -top-1 -right-1 h-3 w-3 bg-destructive rounded-full text-[10px] font-bold text-destructive-foreground flex items-center justify-center">
                  3
                </span>
              </Button>

              {showNotifications && (
                <div className={cn(
                  "absolute right-0 top-full mt-2 w-80 bg-popover border rounded-lg shadow-lg z-50",
                  "animate-in slide-in-from-top-2 duration-200"
                )}>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-sm">Notifications</h3>
                      <Button variant="ghost" size="sm" className="text-xs h-6 px-2">
                        Mark all read
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <div className="p-3 bg-muted/50 rounded-lg text-sm">
                        <p className="font-medium">New AWB #AWB-2024-001</p>
                        <p className="text-muted-foreground text-xs">Created 5 minutes ago</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg text-sm">
                        <p className="font-medium">Manifest #MAN-001 completed</p>
                        <p className="text-muted-foreground text-xs">2 hours ago</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg text-sm">
                        <p className="font-medium">Shipment delivered</p>
                        <p className="text-muted-foreground text-xs">1 day ago</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div> */}

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={cn(
                "h-9 w-9 rounded-lg",
                "hover:bg-accent hover:text-accent-foreground",
                "focus-visible:ring-2 focus-visible:ring-ring",
                "transition-all duration-200"
              )}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? (
                <Moon className="h-4 w-4" />
              ) : (
                <Sun className="h-4 w-4" />
              )}
            </Button>
            
            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                  // setShowNotifications(false);
                }}
                className={cn(
                  "flex items-center gap-3 h-10 px-3 rounded-lg",
                  "hover:bg-accent hover:text-accent-foreground",
                  "focus-visible:ring-2 focus-visible:ring-ring",
                  "transition-all duration-200",
                  showUserMenu && "bg-accent"
                )}
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div className="hidden sm:flex flex-col items-start text-left min-w-0">
                  <span className="text-sm font-medium leading-none truncate max-w-32 lg:max-w-none">
                    {user?.name}
                  </span>
                  <span className="text-xs text-muted-foreground capitalize truncate max-w-32 lg:max-w-none">
                    {user?.role?.toLowerCase().replace('_', ' ')}
                  </span>
                </div>
                <ChevronDown className={cn(
                  "hidden sm:block h-4 w-4 text-muted-foreground transition-transform duration-200",
                  showUserMenu && "rotate-180"
                )} />
              </Button>

              {/* User dropdown menu */}
              {showUserMenu && (
                <div className={cn(
                  "absolute right-0 top-full mt-2 w-64 bg-popover border rounded-lg shadow-lg z-50",
                  "animate-in slide-in-from-top-2 duration-200"
                )}>
                  <div className="p-2">
                    {/* User info header */}
                    <div className="px-3 py-3 border-b border-border/50 mb-2">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{user?.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                          <p className="text-xs text-muted-foreground capitalize mt-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full inline-block">
                            {user?.role?.toLowerCase().replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu items */}
                    <div className="space-y-1">
                      <button
                        onClick={handleProfileClick}
                        className={cn(
                          "flex items-center w-full px-3 py-2.5 text-sm text-left rounded-md",
                          "hover:bg-accent hover:text-accent-foreground",
                          "focus:outline-none focus:bg-accent focus:text-accent-foreground",
                          "transition-colors duration-200"
                        )}
                      >
                        <Settings className="mr-3 h-4 w-4" />
                        Profile Settings
                      </button>
                      <div className="my-1 border-t border-border/50" />
                      <button
                        onClick={handleLogout}
                        className={cn(
                          "flex items-center w-full px-3 py-2.5 text-sm text-left rounded-md",
                          "hover:bg-destructive/10 hover:text-destructive",
                          "focus:outline-none focus:bg-destructive/10 focus:text-destructive",
                          "transition-colors duration-200 text-muted-foreground"
                        )}
                      >
                        <LogOut className="mr-3 h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Profile</DialogTitle>
          </DialogHeader>
          <UserProfile onClose={() => setShowProfileDialog(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;